<script lang="ts" setup>
	import type { ClassNameValue } from 'tailwind-merge'
	import type { ProductContext } from '~/types'
	const { productList } = defineProps<{ productList: ProductContext; btnCls?: ClassNameValue }>()
	const { format } = useCurrency()
	const sendCareMin = computed(() => (e: string) => getMaxMinPrice(e, productList || []))

	const emit = defineEmits<{ (event: 'selected', payload: string): void }>()
	const linkList = computed(() => [
		{
			text: 'SeniorsPlan',
			id: 'senior',
			description: `<span class="font-bold group-hover:text-[var(--link-stroke-color)] transition-all">$${format(
				sendCareMin.value('senior')?.min ?? 0
			)} - </span><span class="font-bold group-hover:text-[var(--link-stroke-color)] transition-all">$${format(
				sendCareMin.value('senior')?.max ?? 0
			)}</span> / person monthly`,
			label: 'Send care home to your elderly ones',
			strokeColor: '#FE7D35',
			bgColor: '#fef8f6',
			icon: 'senior-plan'
		},

		{
			text: 'PrimeCare',
			id: 'primecare',
			description: `<span class="font-bold group-hover:text-[var(--link-stroke-color)] transition-all">$${format(
				sendCareMin.value('primecare')?.min ?? 0
			)} - </span><span class="font-bold group-hover:text-[var(--link-stroke-color)] transition-all">$${format(
				sendCareMin.value('primecare')?.max ?? 0
			)}</span> / person quarterly`,
			label: 'Suitable for your family and loved',
			strokeColor: '#296C5D',
			bgColor: '#f2fffa',
			icon: 'primecare'
		}
	])
</script>

<template>
	<div class="flex items-stretch gap-x-[24px] flex-wrap gap-y-[10.5px]">
		<button
			v-for="(item, index) in linkList"
			:key="`links-${index}`"
			type="button"
			:aria-label="`View more about ${item.text}`"
			:class="
				cn(
					'lg:max-w-[270px] w-full py-[11px] border-[0.4px] group rounded-lg px-[12px] relative inline-flex items-center justify-between lg:min-h-[61px] bg-[var(--link-bg-color)] border-[var(--link-stroke-color)] text-left overflow-hidden hover:border-[1.2px]',
					btnCls
				)
			"
			:style="{
				'--link-stroke-color': item.strokeColor,
				'--link-bg-color': item.bgColor
			}"
			@click="emit('selected', item.id)"
		>
			<div class="lg:space-y-[24px] lg:block flex items-center gap-x-[14.78px] gap-y-[12px] relative z-10">
				<tools-icon dir="fill" :icon="item.icon" :height="42" :width="42" />
				<div class="relative">
					<p
						class="lg:text-base text-sm text-gray-700 font-semibold group-hover:text-[#070707] custom-transition-2"
					>
						{{ item.text }}
					</p>
					<p class="relative mb-[8px] font-medium text-xs leading-[140%]">{{ item.label }}</p>
					<p
						class="lg:text-xs text-[10px] bg-white inline-flex p-[3px] font-medium"
						:style="{
							'--link-stroke-color': item.strokeColor
						}"
						v-html="item.description"
					/>
				</div>
			</div>

			<div
				class="lg:absolute right-[15px] lg:opacity-0 top-4 custom-transition-2 group-hover:opacity-100 lg:group-hover:translate-x-[2px]"
			>
				<tools-icon
					dir="arrows"
					icon="arrow-forward"
					:height="16"
					:width="16"
					:path-class="`stroke-[var(--link-stroke-color)]`"
					svg-class="sm:inline hidden"
				/>
				<tools-icon
					dir="arrows"
					icon="caret-down"
					:height="16"
					:width="16"
					:path-class="`stroke-[var(--link-stroke-color)]`"
					svg-class="sm:hidden inline -rotate-90"
				/>
			</div>
			<tools-icon
				dir="line"
				icon="pattern-4"
				height="200"
				width="65"
				svg-class="absolute -top-[15px] right-5 opacity-[0.2]"
				:stroke="item.strokeColor"
			/>
		</button>
	</div>
</template>

<style></style>
